import React, { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface Testimonial {
  id: number;
  videoUrl: string;
  thumbnail: string;
}

interface CompactTestimonialStripProps {
  testimonials: Testimonial[];
  direction?: 'left' | 'right';
}

const CompactTestimonialStrip: React.FC<CompactTestimonialStripProps> = ({ 
  testimonials,
  direction = 'left'
}) => {
  const [playingVideo, setPlayingVideo] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  
  const bgColor = theme === 'dark' ? '#111111' : '#f4f2f1';
  const borderColor = theme === 'dark' ? '#333333' : '#E0E0E0';
  const cardBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    setIsPaused(false);
  };
  
  // Duplicate for infinite scroll
  const duplicatedTestimonials = [...testimonials, ...testimonials, ...testimonials];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || isPaused) return;

    let animationId: number;
    let scrollPosition = scrollContainer.scrollLeft;
    const scrollSpeed = direction === 'left' ? 0.4 : -0.4;

    const animate = () => {
      if (!scrollContainer || isPaused) return;
      
      scrollPosition += scrollSpeed;
      
      const singleSetWidth = scrollContainer.scrollWidth / 3;
      
      if (direction === 'left' && scrollPosition >= singleSetWidth * 2) {
        scrollPosition = singleSetWidth;
        scrollContainer.scrollLeft = scrollPosition;
      } else if (direction === 'right' && scrollPosition <= 0) {
        scrollPosition = singleSetWidth;
        scrollContainer.scrollLeft = scrollPosition;
      }
      
      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(animate);
    };

    scrollContainer.scrollLeft = scrollContainer.scrollWidth / 3;
    scrollPosition = scrollContainer.scrollLeft;
    
    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isPaused, testimonials.length, direction]);

  return (
    <div className="relative py-6">
      {/* Left fade */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 z-10 pointer-events-none"
        style={{ background: `linear-gradient(to right, ${bgColor} 0%, transparent 100%)` }}
      />
      
      {/* Right fade */}
      <div 
        className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 z-10 pointer-events-none"
        style={{ background: `linear-gradient(to left, ${bgColor} 0%, transparent 100%)` }}
      />
      
      <div 
        ref={scrollRef}
        className={`flex gap-2 sm:gap-3 overflow-x-auto scrollbar-hide ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{ scrollBehavior: 'auto' }}
      >
        {duplicatedTestimonials.map((testimonial, index) => (
          <div
            key={`${testimonial.id}-${index}`}
            className="relative flex-shrink-0 overflow-hidden group cursor-pointer w-[80px] sm:w-[140px]"
            style={{ 
              backgroundColor: cardBgColor,
              border: `1px solid ${borderColor}`, 
              borderRadius: '12px',
              aspectRatio: '9/16',
            }}
            onClick={() => setPlayingVideo(playingVideo === index ? null : index)}
          >
            <div className="w-full h-full">
              {playingVideo === index ? (
                <video
                  src={testimonial.videoUrl}
                  className="w-full h-full object-cover"
                  autoPlay
                  controls
                />
              ) : (
                <>
                  <img
                    src={testimonial.thumbnail}
                    alt={`Testimonial ${testimonial.id}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute bottom-1.5 left-1.5 sm:bottom-2 sm:left-2">
                    <div 
                      className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-white/90 hover:bg-white transition-colors rounded-full"
                    >
                      <Play className="w-2.5 h-2.5 sm:w-3 sm:h-3 ml-0.5" style={{ color: '#111111' }} fill="#111111" />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompactTestimonialStrip;
